// Building blocks for the landing page: lazy looping video, the phone frame, icons.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ASSET } from "./content";

/** Live `prefers-reduced-motion`. */
export function useReducedMotion() {
  const query = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
  const [reduced, setReduced] = useState(!!query?.matches);
  useEffect(() => {
    if (!query) return;
    const on = () => setReduced(query.matches);
    query.addEventListener?.("change", on);
    return () => query.removeEventListener?.("change", on);
  }, [query]);
  return reduced;
}

/**
 * A muted product loop (public/landing/<name>.webm|mp4 + <name>.jpg poster).
 * - Nothing downloads until it is near the viewport (the poster is a lazy <img>, sources attach on approach).
 * - Plays only while on screen; pauses when scrolled away.
 * - Reduced motion: shows the poster and a Play button, never autoplays.
 * - Always has a pause control (WCAG 2.2.2).
 */
export function LoopVideo({ name, label, eager = false, className = "", width, height }: { name: string; label: string; eager?: boolean; className?: string; width: number; height: number }) {
  const wrap = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const reduced = useReducedMotion();
  const [near, setNear] = useState(eager);
  const [onScreen, setOnScreen] = useState(eager);
  const [choice, setChoice] = useState<"play" | "pause" | null>(null);
  const [ready, setReady] = useState(false);
  const want = choice ? choice === "play" : !reduced;

  useEffect(() => {
    const el = wrap.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNear(true);
      setOnScreen(true);
      return;
    }
    const nearIo = new IntersectionObserver(([e]) => e.isIntersecting && setNear(true), { rootMargin: "400px 0px" });
    const seenIo = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { threshold: 0.2 });
    nearIo.observe(el);
    seenIo.observe(el);
    return () => {
      nearIo.disconnect();
      seenIo.disconnect();
    };
  }, []);

  const load = near && want;
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    if (want && onScreen) v.play().catch(() => setChoice("pause"));
    else v.pause();
  }, [load, want, onScreen]);

  return (
    <div ref={wrap} className={`lp-video ${ready ? "is-ready" : ""} ${className}`}>
      <img src={`${ASSET}/${name}.jpg`} alt={label} width={width} height={height} loading={eager ? "eager" : "lazy"} decoding="async" fetchPriority={eager ? "high" : "auto"} />
      {load && (
        <video ref={video} muted loop playsInline preload="auto" aria-hidden="true" tabIndex={-1} width={width} height={height} onPlaying={() => setReady(true)}>
          <source src={`${ASSET}/${name}.webm`} type="video/webm" />
          <source src={`${ASSET}/${name}.mp4`} type="video/mp4" />
        </video>
      )}
      <button type="button" className="lp-video-toggle" aria-label={want ? `Pause: ${label}` : `Play: ${label}`} onClick={() => setChoice(want ? "pause" : "play")}>
        {want ? <IconPause /> : <IconPlay />}
      </button>
    </div>
  );
}

export function PhoneFrame({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`lp-phone ${className}`}>
      <div className="lp-phone-screen">
        {children}
        <span className="lp-phone-island" aria-hidden="true" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ icons */

const svgProps = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };

export const IconPlay = () => (
  <svg {...svgProps}>
    <path d="M8 5.5v13l10-6.5z" fill="currentColor" />
  </svg>
);
export const IconPause = () => (
  <svg {...svgProps}>
    <path d="M9 6v12M15 6v12" />
  </svg>
);
export const IconCheck = () => (
  <svg {...svgProps}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);
export const IconMic = () => (
  <svg {...svgProps}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" />
  </svg>
);
export const IconFrame = () => (
  <svg {...svgProps}>
    <path d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);
export const IconFilm = () => (
  <svg {...svgProps}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4" />
  </svg>
);
